import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '@/config/api';

// In-memory token store for Excel Bearer auth (never persisted to localStorage)
export const excelTokenStore = {
  accessToken: null as string | null,
  refreshToken: null as string | null,
  clear() {
    this.accessToken = null;
    this.refreshToken = null;
  },
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach Bearer token only for all routes except /login
apiClient.interceptors.request.use((config) => {
  const url = config.url || '';
  const isLogin =
    url.includes('login') || url.startsWith('/login') || url.startsWith('login');

  if (!isLogin && excelTokenStore.accessToken) {
    config.headers = config.headers ?? {};
    config.headers['Authorization'] = `Bearer ${excelTokenStore.accessToken}`;
  }

  return config;
});

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: () => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error?: unknown) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  refreshQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const url = originalRequest.url || '';
    const isExcelRoute = url.startsWith('/excel/api/');
    const isAuthEndpoint =
      url.includes('/login') ||
      url.includes('/refresh-token') ||
      url.includes('/logout');

    if (error.response?.status !== 401 || isAuthEndpoint || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Excel Bearer token refresh
    if (isExcelRoute) {
      if (!excelTokenStore.refreshToken) {
        excelTokenStore.clear();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: () => resolve(apiClient(originalRequest)),
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await apiClient.post(API_ENDPOINTS.EXCEL_AUTH.REFRESH_TOKEN, {
          refreshToken: excelTokenStore.refreshToken,
        });
        const { accessToken, refreshToken } = response.data.data;
        excelTokenStore.accessToken = accessToken;
        excelTokenStore.refreshToken = refreshToken;
        processQueue();
        return apiClient(originalRequest);
      } catch (refreshError) {
        excelTokenStore.clear();
        processQueue(refreshError);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Web cookie-based refresh (unchanged)
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: () => resolve(apiClient(originalRequest)),
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      console.log('401 received, trying refresh for:', url);
      await apiClient.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN);
      processQueue();
      return apiClient(originalRequest);
    } catch (refreshError) {
      console.error('Refresh token request failed:', refreshError);
      processQueue(refreshError);
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
